import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Gauge, Wrench, AlertTriangle, Truck, ShieldAlert, FileWarning, Briefcase,
    AlertCircle, Loader2, ChevronRight, BarChart3, TrendingUp, TrendingDown,
    KeyRound,
    Sparkles,
    Fuel, Activity,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import dict from "../i18n/es-MX";

function KpiCard({ label, value, icon: Icon, accent, to, navigate, testid, badgePulse }) {
    const clickable = !!to;
    return (
        <button
            type="button"
            onClick={clickable ? () => navigate(to) : undefined}
            data-testid={testid}
            disabled={!clickable}
            className={[
                "text-left rounded-2xl border bg-slate-900/60 p-5 transition relative group",
                clickable
                    ? "border-slate-800 hover:border-brand-orange hover:bg-slate-900 cursor-pointer"
                    : "border-slate-800 cursor-default",
            ].join(" ")}
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
                <Icon size={18} className={accent || "text-brand-orange"} />
            </div>
            <div className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-50">
                {value}
            </div>
            {clickable && (
                <ChevronRight
                    size={16}
                    className="absolute bottom-3 right-3 text-slate-600 group-hover:text-brand-orange transition"
                />
            )}
            {badgePulse && value > 0 && (
                <span className="absolute top-3 right-12 inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"/>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"/>
                </span>
            )}
        </button>
    );
}

import { fmtDate } from "../lib/dateFormat";

function nextDueText(a) {
    const out = [];
    const nd = a.next_due_meter || {};
    if (nd.odometer_km != null) out.push(`${nd.odometer_km.toLocaleString("es-MX")} km`);
    if (nd.hour_meter_horas != null) out.push(`${nd.hour_meter_horas} h`);
    if (a.next_due_date) out.push(fmtDate(a.next_due_date));
    return out.join(" • ") || "—";
}

function RentalsExpiringWidget() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/dashboard/rentals-expiring", { params: { days: 7 } })
            .then(r => setRows(r.data || []))
            .catch(() => setErr(dict.rentalsExpiring.loadError))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="rounded-2xl border border-slate-800 p-6 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (err) return <div data-testid="rentals-expiring-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{err}</div>;
    if (rows.length === 0) return (
        <div data-testid="rentals-expiring-empty" className="rounded-2xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500">
            {dict.rentalsExpiring.empty}
        </div>
    );
    const fmtMXN0 = (n) => new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN",maximumFractionDigits:0}).format(n||0);
    const fmtDateShort = fmtDate;
    return (
        <div className="rounded-2xl border border-violet-500/30 overflow-hidden" data-testid="rentals-expiring-widget">
            <ul>
                {rows.map((r, idx) => {
                    const expired = r.days_remaining < 0;
                    const today = r.days_remaining === 0;
                    const color = expired ? "text-red-400" : today ? "text-brand-orange" : "text-violet-300";
                    return (
                        <li key={r.job_id} className={`flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-900/40 ${idx > 0 ? "border-t border-slate-800" : ""}`} data-testid={`rental-expiring-${r.job_id}`}>
                            <Link to={`/trabajos/${r.job_id}`} className="min-w-0 flex items-center gap-2 hover:text-slate-100">
                                <KeyRound size={14} className={color}/>
                                <div className="min-w-0">
                                    <div className="text-sm text-slate-100 truncate"><span className="font-mono text-xs text-slate-500">{r.code}</span> · {r.client_name || "—"}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                                        {(r.asset_nicknames || []).join(", ")} · {dict.rentalsExpiring.period} {r.rental_periods_completed + 1}/{r.rental_period_count} · {fmtMXN0(r.rental_amount_per_period_mxn)} · {fmtDateShort(r.rental_current_period_end)}
                                    </div>
                                </div>
                            </Link>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${r.rental_auto_renew?"bg-emerald-500/15 text-emerald-300 border-emerald-500/40":"bg-slate-800 text-slate-300 border-slate-700"}`}>
                                    {r.rental_auto_renew ? dict.rentalsExpiring.autoRenewOn : dict.rentalsExpiring.autoRenewOff}
                                </span>
                                <span className={`text-xs font-mono ${color}`}>
                                    {today ? dict.rentalsExpiring.today : expired ? `${dict.rentalsExpiring.overdue} (${Math.abs(r.days_remaining)})` : `${r.days_remaining} ${dict.rentalsExpiring.daysLabel}`}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function OnboardingWizardMaybe({ navigate }) {
    const [state, setState] = useState(null);
    const [dismissing, setDismissing] = useState(false);
    useEffect(() => {
        api.get("/onboarding/progress").then(r => setState(r.data)).catch(() => setState({ hidden: true }));
    }, []);
    if (!state) return null;
    // Only show in production mode, when not all done, and not dismissed
    if (state.app_mode !== "production" || state.all_done || state.hide_onboarding) return null;

    const stepDefs = [
        { key: "operator", label: "Crear tu primer operador", path: "/operadores/nuevo" },
        { key: "client", label: "Crear tu primer cliente", path: "/clientes/nuevo" },
        { key: "asset", label: "Crear tu primer activo", path: "/activos/nuevo" },
        { key: "job", label: "Registrar tu primer trabajo", path: "/trabajos/nuevo" },
        { key: "users", label: "Invitar a otros usuarios", path: "/usuarios" },
    ];
    const stepsMap = Object.fromEntries((state.steps || []).map(s => [s.key, s.done]));
    const doneCount = Object.values(stepsMap).filter(Boolean).length;

    const dismiss = async () => {
        setDismissing(true);
        try { await api.post("/onboarding/dismiss"); setState({ ...state, hide_onboarding: true }); }
        catch { /* ignore */ }
        finally { setDismissing(false); }
    };

    return (
        <section className="rounded-2xl border-2 border-brand-orange/40 bg-brand-orange/5 p-5 space-y-4" data-testid="onboarding-wizard">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                    <h2 className="font-display text-xl font-bold flex items-center gap-2">
                        <Sparkles size={20} className="text-brand-orange"/> Bienvenido a Flotilla MX
                    </h2>
                    <p className="text-sm text-slate-400 mt-1">Configura tu flotilla en 5 pasos.</p>
                </div>
                <div className="text-xs text-slate-400 font-mono" data-testid="onboarding-progress-label">{doneCount}/5</div>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-brand-orange transition-all" style={{ width: `${(doneCount/5)*100}%` }} data-testid="onboarding-progress-bar"/>
            </div>
            <ul className="space-y-2">
                {stepDefs.map((s, idx) => {
                    const done = stepsMap[s.key];
                    return (
                        <li key={s.key} data-testid={`onboarding-step-${s.key}`}
                            className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${done?"border-emerald-500/40 bg-emerald-500/5":"border-slate-700 bg-slate-900/40"}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done?"bg-emerald-500 text-slate-950":"bg-slate-800 text-slate-400 border border-slate-700"}`}>
                                    {done ? "✓" : idx+1}
                                </span>
                                <span className={done ? "text-slate-300 line-through" : "text-slate-100"}>{s.label}</span>
                            </div>
                            {!done && (
                                <button type="button" onClick={() => navigate(s.path)}
                                    data-testid={`onboarding-cta-${s.key}`}
                                    className="text-xs text-brand-orange hover:underline font-medium shrink-0">
                                    Ir →
                                </button>
                            )}
                        </li>
                    );
                })}
            </ul>
            <div className="text-center">
                <button type="button" onClick={dismiss} disabled={dismissing} data-testid="onboarding-dismiss"
                    className="text-[11px] text-slate-500 hover:text-slate-300 underline underline-offset-2 disabled:opacity-60">
                    Descartar wizard
                </button>
            </div>
        </section>
    );
}

function ExpiringDocsWidget() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    useEffect(() => {
        api.get("/dashboard/expiring-docs", { params: { days: 30 } })
            .then(r => setRows(r.data || []))
            .catch(() => setErr(dict.expiring.loadError))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="rounded-2xl border border-slate-800 p-6 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (err) return <div data-testid="expiring-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{err}</div>;
    if (rows.length === 0) return (
        <div data-testid="expiring-empty" className="rounded-2xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500">
            {dict.expiring.empty}
        </div>
    );
    return (
        <div className="rounded-2xl border border-slate-800 overflow-hidden" data-testid="expiring-widget">
            <ul>
                {rows.map((r, idx) => {
                    const expired = r.days_remaining < 0;
                    const today = r.days_remaining === 0;
                    const color = expired ? "text-red-400" : (r.days_remaining <= 30 ? "text-brand-orange" : "text-slate-300");
                    return (
                        <li key={`${r.asset_id}-${r.doc_type}`} className={`flex items-center justify-between gap-3 px-4 py-3 ${idx > 0 ? "border-t border-slate-800" : ""}`} data-testid={`expiring-${r.asset_id}-${r.doc_type}`}>
                            <Link to={`/activos/${r.asset_id}`} className="min-w-0 flex items-center gap-2 hover:text-slate-100">
                                <FileWarning size={14} className={color}/>
                                <div className="min-w-0">
                                    <div className="text-sm text-slate-100 truncate">{r.nickname || "—"}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{r.doc_type === "seguro" ? dict.expiring.docInsurance : dict.expiring.docRegistration} · {fmtDate(r.expires_at)}</div>
                                </div>
                            </Link>
                            <span className={`text-xs font-mono shrink-0 ${color}`}>
                                {today ? dict.expiring.today : expired ? `${dict.expiring.expired} (${Math.abs(r.days_remaining)} ${dict.expiring.daysLabel})` : `${r.days_remaining} ${dict.expiring.daysLabel}`}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function FleetUtilRow({ r, lowAccent, navigate }) {
    const low = (r.utilization_pct || 0) < 40;
    return (
        <button
            type="button"
            onClick={() => navigate(`/activos/${r.asset_id}?tab=utilization`)}
            data-testid={`fleet-util-row-${r.asset_id}`}
            className="w-full text-left px-3 py-2 flex items-center justify-between gap-3 border-t border-slate-800 hover:bg-slate-900/60 transition"
        >
            <div className="min-w-0 flex items-center gap-2">
                <Truck size={14} className={low || lowAccent ? "text-brand-orange shrink-0" : "text-slate-500 shrink-0"}/>
                <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{r.nickname || "—"}</div>
                    <div className="text-[10px] text-slate-500">
                        {r.days_in_use} {dict.adminPanel.utilization.colInUse.toLowerCase()}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <span className={`font-mono text-sm font-semibold ${low ? "text-brand-orange" : "text-slate-100"}`}>
                    {(r.utilization_pct || 0).toFixed(1)}%
                </span>
                <ChevronRight size={14} className="text-slate-600"/>
            </div>
        </button>
    );
}

function FleetUtilizationWidget() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/finance/asset-utilization")
            .then(r => setRows(r.data || []))
            .catch(() => setErr(dict.adminPanel.utilization.loadError))
            .finally(() => setLoading(false));
    }, []);

    // Backend returns rows already sorted by utilization_pct desc.
    // Top 5 + bottom 3, deduped if fleet is small.
    const { top, bottom } = useMemo(() => {
        if (!rows.length) return { top: [], bottom: [] };
        const top = rows.slice(0, 5);
        const topIds = new Set(top.map(r => r.asset_id));
        const bottom = rows
            .slice()
            .reverse()
            .filter(r => !topIds.has(r.asset_id))
            .slice(0, 3);
        return { top, bottom };
    }, [rows]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-8 text-center">
                <Loader2 className="animate-spin inline text-slate-500"/>
            </div>
        );
    }

    if (err) {
        return (
            <div data-testid="fleet-util-error" className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
                {err}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div
                data-testid="fleet-util-empty"
                className="rounded-2xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500"
            >
                {dict.adminPanel.utilization.empty}
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-slate-800 overflow-hidden" data-testid="fleet-util-widget">
            <div className="px-4 py-2 bg-slate-900/80 text-[10px] uppercase tracking-wider text-emerald-300 inline-flex items-center gap-2 w-full">
                <TrendingUp size={12}/> {dict.adminPanel.utilization.topUsed}
            </div>
            {top.map(r => <FleetUtilRow key={r.asset_id} r={r} navigate={navigate}/>)}
            {bottom.length > 0 && (
                <>
                    <div className="px-4 py-2 bg-slate-900/80 text-[10px] uppercase tracking-wider text-brand-orange inline-flex items-center gap-2 w-full border-t border-slate-800">
                        <TrendingDown size={12}/> {dict.adminPanel.utilization.leastUsed}
                    </div>
                    {bottom.map(r => <FleetUtilRow key={r.asset_id} r={r} lowAccent navigate={navigate}/>)}
                </>
            )}
        </div>
    );
}

export default function AdminPanel() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [alerts, setAlerts] = useState({ overdue: [], due_soon: [] });
    const [anomalies, setAnomalies] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const role = dict.roles[user?.role] || user?.role;

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        const results = await Promise.allSettled([
            api.get("/dashboard/kpis"),
            api.get("/maintenance/alerts"),
            api.get("/alerts/anomalies", { params: { limit: 5 } }),
        ]);
        const [k, a, an] = results;
        if (k.status === "fulfilled") setKpis(k.value.data);
        if (a.status === "fulfilled") setAlerts(a.value.data);
        if (an.status === "fulfilled") setAnomalies(an.value.data || { items: [], total: 0 });
        if (results.every(r => r.status === "rejected")) {
            setErr(formatApiError(results[0].reason));
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const topAlerts = [
        ...alerts.overdue.map(a => ({ ...a, kind: "overdue" })),
        ...alerts.due_soon.map(a => ({ ...a, kind: "due_soon" })),
    ].slice(0, 5);

    return (
        <div data-testid="admin-panel" className="space-y-8">
            <header>
                <div className="flex items-baseline gap-3 flex-wrap">
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
                        {dict.adminPanel.title}
                    </h1>
                    <span className="text-xs uppercase tracking-widest text-brand-orange">{role}</span>
                </div>
                <p className="mt-2 text-slate-400">
                    {dict.adminPanel.welcome}{" "}
                    <span className="text-slate-200 font-medium">{user?.full_name || user?.name}</span>
                </p>
            </header>

            {err && (
                <div data-testid="panel-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{err}</div>
            )}

            <OnboardingWizardMaybe navigate={navigate}/>

            <section>
                <h2 className="text-xs uppercase tracking-wider text-slate-400 mb-3">
                    {dict.adminPanel.operationsKpis}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loading || !kpis ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-28 rounded-2xl border border-slate-800 bg-slate-900/40 animate-pulse"/>
                        ))
                    ) : (
                        <>
                            <KpiCard
                                testid="kpi-pending-readings"
                                label={dict.dashboardKpi.pendingReadings}
                                value={kpis.pending_meter_readings}
                                icon={Gauge}
                                accent="text-amber-300"
                                to="/verificacion?status=pendientes"
                                navigate={navigate}
                                badgePulse
                            />
                            <KpiCard
                                testid="kpi-maintenance-overdue"
                                label={dict.dashboardKpi.maintenanceOverdue}
                                value={kpis.maintenance_overdue}
                                icon={AlertTriangle}
                                accent="text-red-400"
                                to="/mantenimiento?tab=alertas&filter=overdue"
                                navigate={navigate}
                                badgePulse
                            />
                            <KpiCard
                                testid="kpi-maintenance-due-soon"
                                label={dict.dashboardKpi.maintenanceDueSoon}
                                value={kpis.maintenance_due_soon}
                                icon={Wrench}
                                accent="text-amber-300"
                                to="/mantenimiento?tab=alertas&filter=due_soon"
                                navigate={navigate}
                            />
                            <KpiCard
                                testid="kpi-insurance-expiring"
                                label={dict.dashboardKpi.insuranceExpiring}
                                value={kpis.insurance_expiring_30d}
                                icon={ShieldAlert}
                                accent="text-sky-400"
                                to="/activos"
                                navigate={navigate}
                            />
                            <KpiCard
                                testid="kpi-registration-expiring"
                                label={dict.dashboardKpi.registrationExpiring}
                                value={kpis.registration_expiring_30d}
                                icon={FileWarning}
                                accent="text-sky-400"
                                to="/activos"
                                navigate={navigate}
                            />
                            <KpiCard
                                testid="kpi-jobs-in-progress"
                                label={dict.dashboardKpi.jobsInProgress}
                                value={kpis.jobs_in_progress}
                                icon={Briefcase}
                                accent="text-emerald-400"
                                to="/trabajos"
                                navigate={navigate}
                            />
                        </>
                    )}
                </div>
            </section>

            <section data-testid="rentals-expiring-widget">
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                        <KeyRound size={18} className="text-violet-300"/>
                        {dict.rentalsExpiring.title}
                    </h2>
                    <span className="text-xs text-slate-500">{dict.rentalsExpiring.subtitle}</span>
                </div>
                <RentalsExpiringWidget/>
            </section>

            <section data-testid="expiring-docs-section">
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                        <FileWarning size={18} className="text-brand-orange"/>
                        {dict.expiring.title}
                    </h2>
                    <span className="text-xs text-slate-500">{dict.expiring.subtitle}</span>
                </div>
                <ExpiringDocsWidget/>
            </section>

            <section>
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                        <BarChart3 size={18} className="text-brand-orange"/>
                        {dict.adminPanel.utilization.title}
                    </h2>
                    <span className="text-xs text-slate-500">{dict.adminPanel.utilization.subtitle}</span>
                </div>
                <FleetUtilizationWidget/>
            </section>

            <section data-testid="anomalies-section">
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Activity size={18} className="text-brand-orange"/>
                        {dict.adminPanel.anomalies.title}
                    </h2>
                    <span className="text-xs text-slate-500">{dict.adminPanel.anomalies.subtitle}</span>
                </div>
                {loading ? (
                    <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>
                ) : (anomalies.items || []).length === 0 ? (
                    <div
                        data-testid="anomalies-empty"
                        className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500"
                    >
                        {dict.adminPanel.anomalies.empty}
                    </div>
                ) : (
                    <div data-testid="anomalies-list" className="rounded-2xl border border-slate-800 divide-y divide-slate-800 overflow-hidden">
                        {(anomalies.items || []).map(a => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => navigate(a.link)}
                                data-testid={`anomaly-item-${a.id}`}
                                className="w-full text-left p-4 flex items-center justify-between gap-3 bg-amber-500/5 hover:bg-amber-500/10 transition"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    {a.type === "fuel_anomaly"
                                        ? <Fuel size={16} className="text-amber-300 shrink-0"/>
                                        : <Gauge size={16} className="text-amber-300 shrink-0"/>}
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-100 truncate">
                                            {a.entity_label}
                                        </div>
                                        <div className="text-xs mt-0.5 text-amber-200/80 truncate">
                                            {a.description}
                                        </div>
                                    </div>
                                </div>
                                <span className="shrink-0 px-2 h-5 text-[10px] rounded-full border inline-flex items-center uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40">
                                    {a.type === "fuel_anomaly" ? dict.adminPanel.anomalies.typeFuel : dict.adminPanel.anomalies.typeMeter}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <div className="flex items-baseline justify-between mb-3">
                    <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                        <AlertCircle size={18} className="text-brand-orange" />
                        {dict.adminPanel.alerts}
                    </h2>
                    {(alerts.total_overdue_count + alerts.total_due_soon_count) > 5 && (
                        <button
                            type="button"
                            onClick={() => navigate("/mantenimiento?tab=alertas")}
                            data-testid="alerts-view-all"
                            className="text-xs uppercase tracking-wider text-brand-orange hover:underline"
                        >
                            {dict.adminPanel.viewAllAlerts}
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-8"><Loader2 className="animate-spin inline text-slate-500"/></div>
                ) : topAlerts.length === 0 ? (
                    <div
                        data-testid="alerts-empty"
                        className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500"
                    >
                        {dict.adminPanel.noActiveAlerts}
                    </div>
                ) : (
                    <div data-testid="alerts-list" className="rounded-2xl border border-slate-800 divide-y divide-slate-800 overflow-hidden">
                        {topAlerts.map(a => {
                            const isOverdue = a.kind === "overdue";
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => navigate(`/mantenimiento?tab=alertas&filter=${a.kind}`)}
                                    data-testid={`alert-item-${a.id}`}
                                    className={[
                                        "w-full text-left p-4 flex items-center justify-between gap-3 transition",
                                        isOverdue
                                            ? "bg-red-500/5 hover:bg-red-500/10"
                                            : "bg-amber-500/5 hover:bg-amber-500/10",
                                    ].join(" ")}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Truck size={16} className={isOverdue ? "text-red-300 shrink-0" : "text-amber-300 shrink-0"}/>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-100 truncate">
                                                {a.asset_nickname} • {a.label}
                                            </div>
                                            <div className={`text-xs mt-0.5 ${isOverdue ? "text-red-200/80" : "text-amber-200/80"}`}>
                                                {nextDueText(a)}
                                            </div>
                                        </div>
                                    </div>
                                    <span className={[
                                        "shrink-0 px-2 h-5 text-[10px] rounded-full border inline-flex items-center uppercase tracking-wider",
                                        isOverdue
                                            ? "bg-red-500/15 text-red-300 border-red-500/40"
                                            : "bg-amber-500/15 text-amber-300 border-amber-500/40",
                                    ].join(" ")}>
                                        {isOverdue ? dict.maintenance.overdue : dict.maintenance.dueSoon}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
